/**
 * Member form model (Beta-1 plan P4-09 / P4-13). Pure helpers (no Firebase — unit-tested) shared
 * by Add Member, Edit Member and the roster import: which units a role needs, how the form maps to the callable payload,
 * and back from a stored member.
 *
 *   form = { firstName, lastName, displayName, email, title, role, siteIds[], deptIds[], nwtIds[], notify }
 *
 * Scope rules (Tim D07.1 / P4-09): NWT required for FLS and Individual Contributor; department for
 * a DH; site for a site manager; org-wide roles need nothing. Several units may be picked for one
 * scoped role (P4-13: a DH over two departments) — the first is `scopeId`, all of them `scopeIds`.
 * Site and department are always stored (derived from the NWT/department when not picked) so
 * scoping, TRL entry and rollups work without lookups.
 */
export const SCOPE_UNIT_BY_ROLE = Object.freeze({
  org_admin: null,
  org_it: null,
  executive: null,
  site_manager: 'site',
  department_lead: 'department',
  team_lead: 'team',
  respondent: 'team',
});

export function unitForRole(role) {
  return SCOPE_UNIT_BY_ROLE[role] ?? null;
}

export function emptyMemberForm() {
  return { firstName: '', lastName: '', displayName: '', email: '', title: '', role: 'respondent', siteIds: [], deptIds: [], nwtIds: [], notify: true };
}

export function autoDisplayName(firstName, lastName) {
  return `${String(firstName || '').trim()} ${String(lastName || '').trim()}`.trim();
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function validateMemberForm(form, { requireEmail = true } = {}) {
  const errors = {};
  if (!String(form.firstName || '').trim()) errors.firstName = 'First name is required.';
  if (!String(form.lastName || '').trim()) errors.lastName = 'Last name is required.';
  if (requireEmail && !EMAIL_RE.test(String(form.email || '').trim())) errors.email = 'Enter a valid email address.';
  if (!Object.prototype.hasOwnProperty.call(SCOPE_UNIT_BY_ROLE, form.role)) errors.role = 'Choose a role.';
  const unit = unitForRole(form.role);
  if (unit === 'site' && !(form.siteIds || []).length) errors.siteIds = 'Pick at least one site.';
  if (unit === 'department' && !(form.deptIds || []).length) errors.deptIds = 'Pick at least one department.';
  if (unit === 'team' && !(form.nwtIds || []).length) errors.nwtIds = form.role === 'respondent' ? 'Pick the Natural Work Team this person belongs to.' : 'Pick at least one Natural Work Team.';
  return errors;
}

/** Labels that disambiguate "Operations ×10": "Operations — Houston Plant", "Shift Crew A — Operations, Houston Plant". */
export function unitOptions(org) {
  const sites = org?.sites || [];
  const departments = org?.departments || [];
  const teams = org?.teams || [];
  const siteName = (id) => sites.find((s) => s.id === id)?.name || '';
  return {
    sites: sites.map((s) => ({ id: s.id, label: s.name })),
    departments: departments.map((d) => ({ id: d.id, label: `${d.name} — ${siteName(d.siteId)}`, siteId: d.siteId })),
    teams: teams.map((t) => {
      const dept = departments.find((d) => d.id === t.departmentId);
      return { id: t.id, label: `${t.name} — ${dept?.name || '?'}, ${siteName(dept?.siteId)}`, departmentId: t.departmentId, siteId: dept?.siteId || null };
    }),
  };
}

/** The callable payload: role, primary scope, all scopes, and the derived site/department/NWT. */
export function memberFormToPayload(form, org) {
  const unit = unitForRole(form.role);
  const departments = org?.departments || [];
  const teams = org?.teams || [];
  let scopeIds = [];
  if (unit === 'site') scopeIds = [...(form.siteIds || [])];
  if (unit === 'department') scopeIds = [...(form.deptIds || [])];
  if (unit === 'team') scopeIds = [...(form.nwtIds || [])];
  // Tim 26 Oct (D07.1 r3): an org-wide role (Executive, Org Admin, Org IT) may still name a home NWT
  // such as "Executive Team" so they appear in a team and can take that team's surveys.
  const nwtId = unit === 'team' ? scopeIds[0] || null : unit === null ? (form.nwtIds || [])[0] || null : null;
  const team = nwtId ? teams.find((t) => t.id === nwtId) : null;
  const deptId = unit === 'department' ? scopeIds[0] || null : team?.departmentId || (form.deptIds || [])[0] || null;
  const dept = deptId ? departments.find((d) => d.id === deptId) : null;
  const siteId = unit === 'site' ? scopeIds[0] || null : dept?.siteId || (form.siteIds || [])[0] || null;
  return {
    firstName: String(form.firstName || '').trim(),
    lastName: String(form.lastName || '').trim(),
    displayName: String(form.displayName || '').trim() || autoDisplayName(form.firstName, form.lastName),
    email: String(form.email || '').trim().toLowerCase(),
    title: String(form.title || '').trim(),
    role: form.role,
    scopeId: unit ? scopeIds[0] || null : null,
    scopeIds: unit && scopeIds.length > 1 ? scopeIds : null,
    siteId,
    deptId,
    nwtId,
    notify: form.notify !== false,
  };
}

/** A stored member (membersIndex row) back into the form shape for Edit. */
export function memberToForm(member, org) {
  const f = emptyMemberForm();
  if (!member) return f;
  const unit = unitForRole(member.role);
  const all = Array.isArray(member.scopeIds) && member.scopeIds.length ? member.scopeIds : member.scopeId ? [member.scopeId] : [];
  const teams = org?.teams || [];
  const departments = org?.departments || [];
  Object.assign(f, {
    firstName: member.firstName || (member.displayName || '').split(/\s+/)[0] || '',
    lastName: member.lastName || (member.displayName || '').split(/\s+/).slice(1).join(' ') || '',
    displayName: member.displayName || '',
    email: member.email || '',
    title: member.title || '',
    role: member.role || 'respondent',
    notify: true,
  });
  if (unit === 'site') f.siteIds = all;
  if (unit === 'department') f.deptIds = all;
  if (unit === 'team') f.nwtIds = all.length ? all : member.nwtId ? [member.nwtId] : [];
  if (!f.nwtIds.length && member.nwtId) f.nwtIds = [member.nwtId];
  if (!f.deptIds.length && member.deptId) f.deptIds = [member.deptId];
  if (!f.siteIds.length && member.siteId) f.siteIds = [member.siteId];
  // derive site/department for display when only the NWT is stored
  const t = f.nwtIds[0] ? teams.find((x) => x.id === f.nwtIds[0]) : null;
  if (t && !f.deptIds.length) f.deptIds = [t.departmentId];
  const d = f.deptIds[0] ? departments.find((x) => x.id === f.deptIds[0]) : null;
  if (d && !f.siteIds.length) f.siteIds = [d.siteId];
  return f;
}

/** "Shift Crew A — Operations, Houston Plant" for a member row (units resolved from stored ids or scope). */
export function memberUnitLabel(member, org) {
  const o = unitOptions(org);
  const unit = unitForRole(member?.role);
  const ids = Array.isArray(member?.scopeIds) && member.scopeIds.length ? member.scopeIds : member?.scopeId ? [member.scopeId] : [];
  const pick = (list, id) => list.find((x) => x.id === id)?.label;
  if (unit === 'site') return ids.map((id) => pick(o.sites, id) || 'Unknown site').join(' · ');
  if (unit === 'department') return ids.map((id) => pick(o.departments, id) || 'Unknown department').join(' · ');
  if (unit === 'team') {
    const teamIds = ids.length ? ids : member?.nwtId ? [member.nwtId] : [];
    if (teamIds.length) return teamIds.map((id) => pick(o.teams, id) || 'Unknown NWT').join(' · ');
  }
  if (member?.deptId) return pick(o.departments, member.deptId) || '';
  if (member?.siteId) return pick(o.sites, member.siteId) || '';
  return '';
}
